import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, type Subscription, takeUntil } from 'rxjs';

import { Widgets } from '@common/enums';
import { ToolbarWidgetComponent as SpToolbarWidgetComponent } from '@ui/widget';

import type { WebWidgetModel } from '@web/types';

import { EDU_FREE_TARIFF, tariffsCssClasses } from '@web/core/constants/tariff.const';
import { UserFacade } from '@web/store/user/user.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PaidFeaturesModalComponent } from '@web/shared/modals/paid-features-modal/paid-features-modal.component';

@Component({
    selector: 'web-toolbar-widget',
    imports: [CommonModule, SpToolbarWidgetComponent, TranslateModule],
    templateUrl: './web-toolbar-widget.component.html',
    styleUrls: ['./web-toolbar-widget.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolbarWidgetComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public widget!: WebWidgetModel;
    @Input() public eduTariff!: string;
    @Input() public isAdmin!: boolean;
    @Input() public isEduSite!: boolean;
    @Input() public isDisabledLogoWidget!: boolean;

    @Output() public onClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
    @Output() public showPaidFeaturesModalEvent: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

    public readonly siteFacade: SiteFacade = inject(SiteFacade);
    public readonly userFacade: UserFacade = inject(UserFacade);
    public readonly translateService: TranslateService = inject(TranslateService);
    public readonly settingsFacade: SettingsFacade = inject(SettingsFacade);
    public readonly modalService: BsModalService = inject(BsModalService);

    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly unsubscribe$ = new Subject<void>();

    private accessSubscription: Subscription | null = null;

    public nextTariffTranslation: string;
    public badgeTariffClass: string;
    public hasAccess: boolean;
    public isEnableEComm: boolean;

    public get isEduTariffFree(): boolean {
        return this.eduTariff === EDU_FREE_TARIFF;
    }

    public get testimonialDisabled(): boolean {
        return !this.isAdmin && this.isEduTariffFree && this.widget.name === Widgets.TESTIMONIAL;
    }

    /**
     * Edu sites ignore site plan restrictions, except for the code widget:
     * it stays paid-only and is unlocked by either a paid Courses plan or a paid site plan.
     */
    public get isTariffCheckSkipped(): boolean {
        return this.isEduSite && !(this.widget?.name === Widgets.CODE && this.isEduTariffFree);
    }

    public get productDisabled(): boolean {
        return !this.isEnableEComm && this.widget.name === Widgets.PRODUCT_CARD;
    }

    public get catalogDisabled(): boolean {
        return !this.isEnableEComm && this.widget.name === Widgets.CATALOG_PRODUCTS;
    }

    public get logoWidgetDisabled(): boolean {
        return this.isDisabledLogoWidget && this.widget.name === Widgets.LOGO;
    }

    public get disabled(): boolean {
        if (this.isAdmin) {
            return false;
        }

        return (
            !this.widget.permission.isReleased ||
            this.widget.params.disabled ||
            this.testimonialDisabled ||
            this.logoWidgetDisabled ||
            this.productDisabled ||
            this.catalogDisabled
        );
    }

    public get disabledTooltip(): string {
        if (this.logoWidgetDisabled) {
            return 'block_has_logo_tooltip';
        }

        if (this.productDisabled || this.catalogDisabled) {
            return 'widget_product_enabled_tooltip';
        }

        return this.disabled || !this.hasAccess ? this.widget.params.disabledTooltip || this.nextTariffTranslation : '';
    }

    public ngOnInit(): void {
        this.settingsFacade.shopSettings$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((settings) => (this.isEnableEComm = settings?.enabled));
    }

    /**
     * The widget list is built once from a cached stream, while `isEduSite` and `eduTariff` arrive with the site
     * and user data — so the access state has to be recalculated whenever those inputs land, not only on init.
     */
    public ngOnChanges(): void {
        this.updateAccess();
    }

    public getWidgetTitle(): string {
        if (this.widget.name === Widgets.PAYMENT_FORM && this.isEduSite) {
            return 'tariffs_widget_label';
        }

        return this.widget.label;
    }

    public showPaidFeaturesModal(): void {
        if (!this.hasAccess) {
            setTimeout(() => {
                this.modalService.show(PaidFeaturesModalComponent, { class: 'modal-lg', backdrop: true });
            }, 300);
        }
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private updateAccess(): void {
        if (!this.widget) {
            return;
        }

        this.accessSubscription?.unsubscribe();

        if (this.isTariffCheckSkipped) {
            this.hasAccess = true;
            this.nextTariffTranslation = null;
            this.badgeTariffClass = null;
            this.changeDetectorRef.markForCheck();

            return;
        }

        this.accessSubscription = this.userFacade
            .hasAccessToElement(this.widget.name)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(({ hasAccess, nextTariff }) => {
                this.nextTariffTranslation = nextTariff
                    ? this.translateService.instant('widget_tariff_restriction_less_pro_text')
                    : null;
                this.badgeTariffClass = nextTariff ? tariffsCssClasses[nextTariff] : null;
                this.hasAccess = hasAccess;
                this.changeDetectorRef.markForCheck();
            });
    }
}
